import {useCallback, useEffect, useRef, useState} from 'react'

/** Maximum composer content height as a fraction of the viewport height. */
export const COMPOSER_MAX_VIEWPORT_RATIO = 0.5
/** Smallest manual height (and the drag/keyboard collapse threshold) in px, ~5 lines. */
export const COMPOSER_MIN_HEIGHT_PX = 138
const KEYBOARD_STEP_PX = 40
const CSS_VAR = '--sticky-composer-content-height'
/**
 * Editor surfaces whose current height seeds a drag, in priority order. The preview wrapper
 * is included because in Preview mode the textarea is kept in the DOM but `display: none`
 * (offsetHeight 0); we pick the first visible one so the drag doesn't seed from 0.
 */
const EDITOR_SELECTOR = 'textarea, .cm-editor, [class*="previewViewerWrapper"]'

/** Maximum height in px: the larger of the minimum or 50% of the viewport. */
const getMaxHeight = (viewportHeight: number) =>
  Math.max(COMPOSER_MIN_HEIGHT_PX, viewportHeight * COMPOSER_MAX_VIEWPORT_RATIO)

const clampHeight = (value: number, maxHeight: number) => Math.min(maxHeight, Math.max(COMPOSER_MIN_HEIGHT_PX, value))

/** The write-mode textarea, whose inline height the hook drives and the native grab handle resizes. */
const getWriteTextarea = (container: HTMLElement | null) =>
  container?.querySelector<HTMLTextAreaElement>('textarea') ?? null

type UseComposerResizeOptions = {
  /** Whether the resizable sticky composer experience is active. */
  enabled: boolean
  /** The sticky composer wrapper whose CSS height variable is updated. */
  containerRef: React.RefObject<HTMLElement | null>
  /**
   * Whether the composer is "active" — it has a draft or is focused. The manual
   * resize height is reset once the composer goes inactive (cleared and blurred),
   * so a new draft (or a reload) returns to the default organic-growth behavior.
   */
  active: boolean
  /** Called when the user drags or keys below the minimum height. */
  onCollapse: () => void
}

type ComposerResizeHandleProps = {
  role: 'separator'
  tabIndex: 0
  'aria-orientation': 'horizontal'
  'aria-valuemin': number
  'aria-valuemax': number
  'aria-valuenow': number
  'aria-valuetext': string
  'aria-label': string
  onPointerDown: (event: React.PointerEvent<HTMLElement>) => void
  onKeyDown: (event: React.KeyboardEvent<HTMLElement>) => void
}

type UseComposerResizeResult = {
  /** Inline style applying the manual height to the wrapper, or undefined when unset/disabled. */
  style: React.CSSProperties | undefined
  /** True once the user has manually resized (so all surfaces should be fixed to the height). */
  isResized: boolean
  /** True while a pointer drag is in progress. */
  isResizing: boolean
  /** Props to spread onto the grab handle element. */
  handleProps: ComposerResizeHandleProps
}

/**
 * Drives the resizable sticky comment composer.
 *
 * By default the composer has no fixed height: the editor grows organically with its
 * content (up to maxHeightLines) and the preview/hybrid surfaces are only capped so a
 * long render can't fill the viewport. Only once the user drags the grab handle (or uses
 * the keyboard) is a fixed pixel height applied to every surface, so write/preview/hybrid
 * all display their content within the chosen height.
 *
 * The manual height lives in component state (never persisted) and is reset when the
 * composer goes inactive (cleared and blurred), so a new draft — or a page reload —
 * returns to organic growth rather than a previously dragged height.
 */
export function useComposerResize({
  enabled,
  containerRef,
  active,
  onCollapse,
}: UseComposerResizeOptions): UseComposerResizeResult {
  // The user's in-session manual height (null = default organic growth). Not persisted.
  const [resizedHeight, setResizedHeight] = useState<number | null>(null)
  const [isResizing, setIsResizing] = useState(false)
  const [viewportHeight, setViewportHeight] = useState(() => (typeof window === 'undefined' ? 0 : window.innerHeight))

  // Reset the manual height when the composer goes inactive (blurred and empty). Done
  // during render (guarded to avoid a loop) rather than in an effect to avoid a cascade.
  if (!active && resizedHeight !== null) {
    setResizedHeight(null)
  }

  // Re-enforce the max when the viewport is resized so a height chosen on a tall window is
  // clamped down on a shorter one. Coalesced to one update per frame to avoid thrash.
  useEffect(() => {
    if (!enabled) return
    let rafId = 0
    const onResize = () => {
      if (rafId) return
      rafId = requestAnimationFrame(() => {
        rafId = 0
        setViewportHeight(window.innerHeight)
      })
    }
    // eslint-disable-next-line github/prefer-observers -- ResizeObserver cannot observe window.innerHeight (viewport)
    window.addEventListener('resize', onResize)
    return () => {
      window.removeEventListener('resize', onResize)
      if (rafId) cancelAnimationFrame(rafId)
    }
  }, [enabled])

  const maxHeight = getMaxHeight(viewportHeight)

  // Keep latest values available to event handlers without recreating them.
  const resizedHeightRef = useRef(resizedHeight)
  const maxHeightRef = useRef(maxHeight)
  useEffect(() => {
    resizedHeightRef.current = resizedHeight
    maxHeightRef.current = maxHeight
  }, [resizedHeight, maxHeight])

  // The last height the hook wrote to the textarea/var. The native-resize observer compares
  // against it to ignore our own programmatic changes (vs. a genuine grab-handle drag).
  const lastAppliedHeightRef = useRef<number | null>(null)
  // Set when the native-resize observer drives an update, so the sync effect below doesn't
  // rewrite the inline height the grab handle just set (which would fight the active drag).
  const fromNativeResizeRef = useRef(false)
  // Mirror of isResizing for the observer, which must not fold in the composer handle's own
  // (programmatic) height changes during a pointer drag.
  const isResizingRef = useRef(false)
  useEffect(() => {
    isResizingRef.current = isResizing
  }, [isResizing])

  // Aborts an in-flight pointer drag's listeners; held in a ref so an unmount mid-drag can
  // tear them down (otherwise pointermove/up would leak and mutate state after unmount).
  const dragAbortRef = useRef<AbortController | null>(null)
  useEffect(() => {
    return () => dragAbortRef.current?.abort()
  }, [])

  // The editor's current rendered height seeds the first drag, so resizing starts from
  // wherever organic growth left it rather than a guessed value. Skip any hidden (zero-height)
  // surface — e.g. the display:none textarea behind the Preview tab — and use the visible one.
  const measureCurrentHeight = useCallback(() => {
    const surfaces = containerRef.current?.querySelectorAll<HTMLElement>(EDITOR_SELECTOR)
    const visible = surfaces && Array.from(surfaces).find(el => el.offsetHeight > 0)
    return visible?.offsetHeight ?? COMPOSER_MIN_HEIGHT_PX
  }, [containerRef])

  // Apply a height to both the CSS var (sizing the pinned preview/hybrid surfaces) and the
  // write textarea's inline height (which the native grab handle also writes to), keeping the
  // two resize affordances on a single height. lastAppliedHeightRef records it so the
  // native-resize observer can tell our own change from a grab-handle drag.
  const applyHeightToDom = useCallback(
    (px: number) => {
      const container = containerRef.current
      if (!container) return
      container.style.setProperty(CSS_VAR, `${px}px`)
      lastAppliedHeightRef.current = px
      const textarea = getWriteTextarea(container)
      if (textarea) textarea.style.height = `${px}px`
    },
    [containerRef],
  )

  // Clear the directly-set height var and textarea height so a collapse returns to organic
  // growth. Needed because both are set imperatively during a drag and aren't owned by React.
  const clearHeightFromDom = useCallback(() => {
    const container = containerRef.current
    if (!container) return
    container.style.removeProperty(CSS_VAR)
    lastAppliedHeightRef.current = null
    const textarea = getWriteTextarea(container)
    if (textarea) textarea.style.height = ''
  }, [containerRef])

  const onPointerDown = useCallback(
    (event: React.PointerEvent<HTMLElement>) => {
      if (!enabled || event.button !== 0) return
      event.preventDefault()
      // preventDefault suppresses the default focus, so focus the handle explicitly to keep
      // it keyboard-operable (ArrowUp/Down) immediately after a pointer user clicks it.
      event.currentTarget.focus()

      const startY = event.clientY
      const startHeight = resizedHeightRef.current ?? measureCurrentHeight()
      const maxAtStart = maxHeightRef.current
      let latestHeight = startHeight
      let collapsing = false
      let moved = false

      const controller = new AbortController()
      dragAbortRef.current = controller

      const onMove = (moveEvent: PointerEvent) => {
        if (!moved) {
          // Defer entering "resizing" until the pointer actually moves, so a plain click on
          // the handle doesn't switch the composer into fixed-height mode. Seed the height
          // var on this first frame so the fixed-height CSS has a value instead of briefly
          // collapsing the editor.
          moved = true
          setIsResizing(true)
          applyHeightToDom(startHeight)
        }
        // Dragging up grows the composer.
        const next = startHeight + (startY - moveEvent.clientY)
        collapsing = next < COMPOSER_MIN_HEIGHT_PX
        latestHeight = clampHeight(next, maxAtStart)
        applyHeightToDom(latestHeight)
      }

      const onUp = () => {
        controller.abort()
        dragAbortRef.current = null
        // A click (or canceled gesture) without any movement leaves the height untouched.
        if (!moved) return
        setIsResizing(false)
        if (collapsing) {
          // Collapsing resets to organic growth rather than committing the clamped minimum,
          // so re-expanding doesn't leave the composer stuck at a fixed minimum height.
          clearHeightFromDom()
          setResizedHeight(null)
          onCollapse()
        } else {
          setResizedHeight(latestHeight)
        }
      }

      const {signal} = controller
      window.addEventListener('pointermove', onMove, {signal})
      window.addEventListener('pointerup', onUp, {signal})
      // Treat an interrupted gesture (e.g. pointercancel) like pointer up so listeners are
      // always cleaned up and any in-progress height is committed.
      window.addEventListener('pointercancel', onUp, {signal})
    },
    [enabled, measureCurrentHeight, applyHeightToDom, clearHeightFromDom, onCollapse],
  )

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLElement>) => {
      if (!enabled) return

      const current = resizedHeightRef.current ?? measureCurrentHeight()
      const max = maxHeightRef.current
      let next: number
      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic -- ARIA separator widget keys are element-scoped, not global commands
      switch (event.key) {
        case 'ArrowUp':
          next = current + KEYBOARD_STEP_PX
          break
        case 'ArrowDown':
          next = current - KEYBOARD_STEP_PX
          break
        case 'Home':
          next = COMPOSER_MIN_HEIGHT_PX
          break
        case 'End':
          next = max
          break
        default:
          return
      }

      event.preventDefault()
      if (next < COMPOSER_MIN_HEIGHT_PX) {
        // Collapsing resets to organic growth (see the pointer-drag path) rather than
        // committing a fixed minimum height.
        clearHeightFromDom()
        setResizedHeight(null)
        onCollapse()
        return
      }
      setResizedHeight(clampHeight(next, max))
    },
    [enabled, measureCurrentHeight, clearHeightFromDom, onCollapse],
  )

  const clampedResizedHeight = resizedHeight === null ? null : clampHeight(resizedHeight, maxHeight)
  const isResized = clampedResizedHeight !== null

  // Keep the write textarea's inline height in sync with the chosen height (covers the
  // keyboard path and viewport re-clamps, which don't go through applyHeightToDom). Skip the
  // write when the change came from the native grab handle, which already set the inline
  // height — rewriting it would interrupt the in-progress drag.
  useEffect(() => {
    if (!enabled) return
    if (fromNativeResizeRef.current) {
      fromNativeResizeRef.current = false
      lastAppliedHeightRef.current = clampedResizedHeight
      return
    }
    const textarea = getWriteTextarea(containerRef.current)
    if (!textarea) return
    if (clampedResizedHeight === null) {
      textarea.style.height = ''
      lastAppliedHeightRef.current = null
    } else {
      textarea.style.height = `${clampedResizedHeight}px`
      lastAppliedHeightRef.current = clampedResizedHeight
    }
  }, [enabled, containerRef, clampedResizedHeight])

  // Fold native textarea grab-handle resizes back into the shared composer height, so both
  // the grab handle and the composer handle drive the same height (and preview/hybrid follow).
  // Only active once resized: in organic mode the textarea grows with content, which would be
  // indistinguishable from a grab-handle drag.
  useEffect(() => {
    if (!enabled || !isResized) return
    const textarea = getWriteTextarea(containerRef.current)
    if (!textarea) return
    // Coalesce the read + state update to one per frame. Reading offsetHeight and calling
    // setState synchronously during observer delivery forces a reflow and can trip a
    // "ResizeObserver loop" stall (notably in WebKit); rAF defers it past the observer pass.
    let rafId = 0
    const observer = new ResizeObserver(() => {
      if (rafId) return
      rafId = requestAnimationFrame(() => {
        rafId = 0
        // Ignore the composer handle's own programmatic changes during a pointer drag.
        if (isResizingRef.current) return
        const height = textarea.offsetHeight
        // Hidden behind the Preview tab (display:none) — don't fold a zero height into the size.
        if (height === 0) return
        // Ignore our own programmatic height writes; only react to genuine grab-handle drags.
        if (lastAppliedHeightRef.current !== null && Math.abs(height - lastAppliedHeightRef.current) < 1) return
        const clamped = clampHeight(height, maxHeightRef.current)
        lastAppliedHeightRef.current = clamped
        fromNativeResizeRef.current = true
        setResizedHeight(clamped)
      })
    })
    observer.observe(textarea)
    return () => {
      observer.disconnect()
      if (rafId) cancelAnimationFrame(rafId)
    }
  }, [enabled, isResized, containerRef])

  return {
    style: enabled && clampedResizedHeight !== null ? {[CSS_VAR]: `${clampedResizedHeight}px`} : undefined,
    isResized,
    isResizing,
    handleProps: {
      role: 'separator',
      tabIndex: 0,
      'aria-orientation': 'horizontal',
      'aria-valuemin': COMPOSER_MIN_HEIGHT_PX,
      'aria-valuemax': Math.round(maxHeight),
      'aria-valuenow': Math.round(clampedResizedHeight ?? COMPOSER_MIN_HEIGHT_PX),
      // In organic mode aria-valuenow falls back to the minimum, which doesn't reflect the
      // actual height; announce "Auto height" until the user fixes a height by resizing.
      'aria-valuetext': clampedResizedHeight === null ? 'Auto height' : `${Math.round(clampedResizedHeight)} pixels`,
      'aria-label': 'Resize comment composer',
      onPointerDown,
      onKeyDown,
    },
  }
}
